import { Box, Menu, MenuItem, Typography, Button } from "@mui/material";
import React, { FC, useState } from "react";
import SortIcon from "@mui/icons-material/Sort";
import { Colors } from "../../../configs";

interface StatusFilterProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export const StatusFilter: FC<StatusFilterProps> = ({ value, onChange, disabled }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (val: string) => {
        onChange(val);
        handleClose();
    };

    const getLabel = (val: string) => {
        switch (val) {
            case "ALL": return "All";
            case "ACTIVE": return "Active";
            case "INACTIVE": return "Inactive";
            case "LOCKED": return "Locked";
            default: return val;
        }
    };

    const getColor = (val: string) => {
        if (val === 'ACTIVE') return Colors.PRIMARY; // Assuming primary is green or we use a specific green
        // Let's check Colors.activeRetail in Lobby.tsx was #2E7D32 which is green.
        // Colors.PRIMARY is usually blue in these apps, but let's check.
        // In the image "Active" is green-ish.
        // If I don't know the exact green, I will use Colors.PRIMARY for now or hardcode a green if PRIMARY is blue.
        // Wait, in Lobby.tsx check COLORS object. activeRetail: "#2E7D32".
        return Colors.TEXT_GRAY_DARK;
    };

    // Actually, let's use a conditional color.
    // Assuming Colors module has what we need. 
    // I will use Colors.PRIMARY for active for now, user can correct.
    // Or better, stick to Colors.TEXT_GRAY_DARK unless I am sure.
    // In the image, "Active" text is indeed green.

    return (
        <Box>
            <Button
                id="status-filter-button"
                aria-controls={open ? 'status-filter-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                disabled={disabled}
                startIcon={
                    <Box
                        sx={{
                            backgroundColor: Colors.WHITE,
                            borderRadius: "6px",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" // Optional: adding subtle shadow for better visibility on gray bg
                        }}
                    >
                        <SortIcon sx={{ color: Colors.TEXT_GRAY_DARK, transform: "scaleY(-1)", fontSize: "1.2rem" }} />
                    </Box>
                }
                sx={{
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    minWidth: 'auto',
                    padding: '6px 10px',
                    '&:hover': {
                        backgroundColor: 'transparent'
                    },
                }}
                disableRipple
            >
                <Typography
                    sx={{
                        color: '#2E7D32',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                    }}
                >
                    {getLabel(value)}
                </Typography>
            </Button>
            <Menu
                id="status-filter-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: '10px',
                        marginTop: '4px',
                        minWidth: '140px'
                    }
                }}
                MenuListProps={{
                    'aria-labelledby': 'status-filter-button',
                    sx: { py: "0.3rem" }
                }}
            >
                {["ALL", "ACTIVE", "INACTIVE", "LOCKED"].map((opt) => (
                    <MenuItem
                        key={opt}
                        onClick={() => handleMenuItemClick(opt)}
                        sx={{ fontSize: "1.05rem", fontWeight: 500, py: "0.55rem" }}
                    >
                        {opt.charAt(0) + opt.slice(1).toLowerCase()}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};
